import React from "react";
import { connect } from "react-redux";
import { Guid } from "guid-typescript";

import { IAppState } from "../../state/Store";
import { initialiseGeographyDetails } from "../../state/core/Geography";

import Address from "../../types/core/Address";
import Country from "../../types/core/Country";
import State from "../../types/core/State";
import DropDown, { DropDownOption } from "../inputform/DropDown";
import Textbox from "../inputform/Textbox";
import Button from "../buttons/Button";
import Configuration from "../../foundation/Configuration";
import WebRequest from "../../communication/WebRequest";

import "./AddressEditor.scss";

/**
 * Fields from the Address model that should be included in the display value.
 * These fields are concatenated with commas to form the visible address string.
 */
const addressEditor_fields = [
    "line1",
    "line2",
    "city",
    "stateName",
    "postcode",
    "countryName",
];

/**
 * Response structure from the AddressFinder autocomplete API.
 * Contains an array of address completion suggestions.
 */
interface AddressAutoCompleteResult {
    /** Array of autocomplete suggestions returned from the API */
    completions: Array<AddressAutoCompleteResultCompletion>;
}

/**
 * Full address details from the AddressFinder info API.
 * Contains the complete structured address information for a selected address.
 */
interface AddressAutoCompleteFullResult {
    /** First line of the street address */
    address_line_1: string;
    /** Second line of the street address (e.g., unit number, building name) */
    address_line_2: string;
    /** City or suburb name */
    locality_name: string;
    /** State or territory abbreviation */
    state_territory: string;
    /** Postal code */
    postcode: string;
}

/**
 * Individual address completion item from the AddressFinder autocomplete API.
 * Represents a single suggested address with identification and display information.
 */
interface AddressAutoCompleteResultCompletion {
    /** Unique identifier for the address suggestion */
    id: string;
    /** Complete address string for display */
    full_address: string;
    /** Canonical identifier for the address */
    canonical_address_id: string;
    /** HTML-highlighted version of the full address with matching text emphasized */
    highlighted_full_address: string;
}

interface AddressEditorProps {
    value?: Address;
    states?: Record<number, State[]>;
    countries?: Country[];
    onChange?: (address: Address) => void;
    initialiseGeographyDetails?: () => void;
}

interface AddressEditorState {
    inline: boolean;
    display: string;
    typeBuffer: string;
    focused: boolean;
    forceOpen: boolean;
    options: DropDownOption[];
}

class AddressEditor extends React.Component<
    AddressEditorProps,
    AddressEditorState
> {
    alphaNumeric =
        "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789.-/\\ ".split(
            "",
        );

    constructor(props: AddressEditorProps) {
        super(props);

        this.state = {
            inline: true,
            display: props.value ? this.parseAddress(props.value) : "",
            typeBuffer: "",
            focused: false,
            forceOpen: false,
            options: [],
        };

        props.initialiseGeographyDetails();
    }

    componentDidUpdate(
        prevProps: Readonly<AddressEditorProps>,
        prevState: Readonly<AddressEditorState>,
        snapshot?: any,
    ): void {
        if (prevProps.value !== this.props.value) {
            this.setState({
                display: this.props.value
                    ? this.parseAddress(this.props.value)
                    : "",
            });
        }
    }

    currentStates(): State[] {
        return (
            Object.entries(this.props.states ?? {}).find(
                entry => entry[0] === this.props.value?.country?.toString(),
            )?.[1] ?? []
        );
    }

    parseAddress(address: Address): string {
        return addressEditor_fields
            .map(field => address[field])
            .filter(value => !!value)
            .join(", ");
    }

    toggleInline() {
        this.setState({
            inline: !this.state.inline,
            display: this.state.inline
                ? ""
                : this.parseAddress(this.props.value),
            typeBuffer: "",
            options: [],
        });
    }

    handleFocus() {
        setTimeout(() => {
            this.setState({ focused: true });
        });
    }

    opId: string;
    handleBlur() {
        const opId = Guid.create().toString();
        this.opId = opId;
        this.setState({ focused: false });
        setTimeout(() => {
            if (!this.state.focused && this.opId === opId) {
                this.setState({ forceOpen: false });
            }
        }, 100);
    }

    async inlineKeyup(event: React.KeyboardEvent<HTMLInputElement>) {
        if (
            this.alphaNumeric.includes(event.key) ||
            event.key === "Backspace" ||
            event.key === "Delete"
        ) {
            let buffer = this.state.typeBuffer;

            switch (event.key) {
                case "Backspace":
                case "Delete":
                    buffer = buffer.slice(0, -1);
                    break;
                default:
                    buffer = buffer + event.key;
            }

            this.setState({
                display: buffer,
                typeBuffer: buffer,
            });

            event.stopPropagation();

            if (buffer.length < 3) {
                this.setState({
                    forceOpen: true,
                    options: [],
                });
                return;
            }

            const response =
                await WebRequest.getType<AddressAutoCompleteResult>(
                    `https://api.addressfinder.io/api/au/address/autocomplete?key=${encodeURIComponent(
                        Configuration.addressServiceKey,
                    )}&q=${encodeURIComponent(buffer)}&format=json&highlight=1`,
                );

            this.setState({
                forceOpen: true,
                options: response.completions.map(completion => ({
                    text: completion.full_address,
                    value: completion.id,
                })),
            });
        }
    }

    async inlineChange(value: string) {
        const response =
            await WebRequest.getType<AddressAutoCompleteFullResult>(
                `https://api.addressfinder.io/api/au/address/info?id=${encodeURIComponent(
                    value,
                )}&key=${encodeURIComponent(
                    Configuration.addressServiceKey,
                )}&format=json`,
            );

        const country =
            this.props.countries?.find(item => item.name === "Australia")?.id ??
            1;
        const state =
            this.props.states?.[country]?.find(
                item => item.name === response.state_territory,
            )?.id ?? 1;
        const returnValue = {
            line1: response.address_line_1,
            line2: response.address_line_2,
            city: response.locality_name,
            state: state,
            stateName: response.state_territory,
            postcode: response.postcode,
            country: country, // Australia country code
            countryName: "Australia",
        } as Address;

        this.setState({
            display: this.parseAddress(returnValue),
            forceOpen: false,
            typeBuffer: "",
            options: [],
        });

        this.changed(returnValue);
    }

    changed(address: Address) {
        this.props.onChange && this.props.onChange(address);
    }
    changeEditAddressLine1(value: string) {
        this.changed({
            ...this.props.value,
            line1: value,
        });
    }
    changeEditAddressLine2(value: string) {
        this.changed({
            ...this.props.value,
            line2: value,
        });
    }
    changeEditAddressCity(value: string) {
        this.changed({
            ...this.props.value,
            city: value,
        });
    }
    changeEditAddressState(value: number) {
        this.changed({
            ...this.props.value,
            state: value,
        });
    }
    changeEditAddressCountry(value: number) {
        this.changed({
            ...this.props.value,
            country: value,
        });
    }
    changeEditAddressPostCode(value: string) {
        this.changed({
            ...this.props.value,
            postcode: value,
        });
    }

    render(): React.ReactNode {
        let classes = "address-editor";

        if (this.state.inline) classes += " inline";

        return (
            <div className={classes}>
                {this.state.inline ? (
                    <>
                        <DropDown
                            value={this.state.display}
                            options={this.state.options}
                            onKeyUp={ev => this.inlineKeyup(ev)}
                            onChange={value => this.inlineChange(value)}
                        />
                        <Button
                            icon="edit"
                            onClick={this.toggleInline.bind(this)}
                        />
                    </>
                ) : (
                    <>
                        <label>Line 1</label>
                        <Textbox
                            autoComplete="address-line1"
                            value={this.props.value?.line1 ?? ""}
                            onChange={value =>
                                this.changeEditAddressLine1(value)
                            }
                        />

                        <label>Line 2</label>
                        <Textbox
                            autoComplete="address-line2"
                            value={this.props.value?.line2 ?? ""}
                            onChange={value =>
                                this.changeEditAddressLine2(value)
                            }
                        />

                        <div className="flex-row spaced">
                            <div className="flex-column spaced">
                                <label>City</label>
                                <Textbox
                                    autoComplete="address-level2"
                                    value={this.props.value?.city ?? ""}
                                    onChange={value =>
                                        this.changeEditAddressCity(value)
                                    }
                                />
                            </div>
                            <div className="flex-column spaced">
                                <label>Country</label>
                                <DropDown
                                    noType={true}
                                    value={this.props.value?.country}
                                    options={this.props.countries.map(
                                        country => ({
                                            text: country.name,
                                            value: country.id,
                                        }),
                                    )}
                                    onChange={value =>
                                        this.changeEditAddressCountry(
                                            Number(value),
                                        )
                                    }
                                />
                            </div>
                            <div className="flex-column spaced">
                                <label>State</label>
                                <DropDown
                                    noType={true}
                                    value={this.props.value?.state}
                                    options={this.currentStates().map(
                                        state => ({
                                            text: state.name,
                                            value: state.id,
                                        }),
                                    )}
                                    onChange={value =>
                                        this.changeEditAddressState(
                                            Number(value),
                                        )
                                    }
                                />
                            </div>
                            <div className="flex-column spaced">
                                <label>Postal Code</label>
                                <Textbox
                                    autoComplete="postal-code"
                                    value={this.props.value?.postcode ?? ""}
                                    onChange={value =>
                                        this.changeEditAddressPostCode(value)
                                    }
                                />
                            </div>
                        </div>

                        <div className="flex-row-reverse top-clearance-xsmall buffer-small">
                            <Button
                                icon="search"
                                onClick={this.toggleInline.bind(this)}
                            />
                        </div>
                    </>
                )}
            </div>
        );
    }
}

const mapStateToProps = (state: IAppState) => {
    const { geography } = state.db;
    return {
        states: geography.states,
        countries: geography.countries,
    };
};

const mapDispatchToProps = dispatch => {
    return {
        initialiseGeographyDetails: () =>
            dispatch(initialiseGeographyDetails()),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(AddressEditor);
